import { useCallback, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Alert, Button, Card, Form, Input, Modal, Select, Space, Tag, Tooltip } from 'antd'
import { ReloadOutlined, SaveOutlined, SyncOutlined } from '@ant-design/icons'
import { useToast } from '../contexts/ToastContext'
import { useUpdateProfileMutation, useProfile } from '../hooks/useProfile'
import { useAuthSession, updateSession } from '../lib/session'
import { normalizePhone } from '../lib/phone'
import { forgotPassword } from '../features/auth/api'
import { useAuthorization } from '../contexts/AuthorizationContext'
import { permissions } from '../types/authorization'
import { featureFlags } from '../config/featureFlags'
import FeatureThemeProvider from '../components/FeatureThemeProvider'
import { AvatarVoicePanel } from '../features/advisory-ai/settings/components/AvatarVoicePanel'
import AuthenticatorSettings from '../features/auth/components/AuthenticatorSettings'
import { AUTHENTICATOR_SETUP_PARAM } from '../features/auth/returnTo'
import McpProfileCard from '../features/mcp/profile/McpProfileCard'

type ProfileFormValues = {
  userId: string
  clientId: string
  firstName: string
  lastName: string
  email: string
  phone: string
  typeId: number
  defaultClientId: string
  defaultPageSize: number
  isTwoFactorLogin: boolean
}

const pageSizeOptions = [10, 20, 50, 100].map((value) => ({ label: String(value), value }))

export default function Profile() {
  const navigate = useNavigate()
  const { addToast } = useToast()
  const session = useAuthSession()
  const { data, isLoading, refetch, isFetching } = useProfile()
  const updateProfileMutation = useUpdateProfileMutation()
  const [form] = Form.useForm<ProfileFormValues>()
  const [searchParams, setSearchParams] = useSearchParams()
  const autoStartAuthenticatorSetup = searchParams.get(AUTHENTICATOR_SETUP_PARAM.key) === AUTHENTICATOR_SETUP_PARAM.value
  const clearAuthenticatorSetupParam = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete(AUTHENTICATOR_SETUP_PARAM.key)
        return params
      },
      { replace: true }
    )
  }, [setSearchParams])
  const { hasAnyPermission } = useAuthorization()
  const canUseAdvisory =
    featureFlags.enableAdvisoryAIMenu &&
    hasAnyPermission([permissions.viewAdvisoryAI, permissions.updateAdvisoryAI])

  useEffect(() => {
    if (!data) return
    form.setFieldsValue(data)
  }, [data, form])

  const handleRefresh = async () => {
    try {
      await refetch()
      addToast('Profile refreshed', 'success')
    } catch (e) {
      addToast(e instanceof Error ? e.message : 'Unable to refresh profile', 'error')
    }
  }

  const handleSave = async () => {
    if (!data) return
    try {
      const values = await form.validateFields()
      const saved = await updateProfileMutation.mutateAsync({
        ...data,
        ...values,
        phone: normalizePhone(values.phone),
      })

      if (saved?.defaultPageSize) {
        updateSession({ defaultPageSize: saved.defaultPageSize })
      }

      addToast('Profile updated successfully', 'success')
      await refetch()
    } catch (e) {
      if (typeof e === 'object' && e !== null && 'errorFields' in e) return
      addToast(e instanceof Error ? e.message : 'Unable to update profile', 'error')
    }
  }

  const handleResetPassword = () => {
    const email = data?.email || session?.email || ''
    if (!email) {
      addToast('Profile email is not available', 'error')
      return
    }

    Modal.confirm({
      title: 'Reset password',
      content:
        'Are you sure you want to reset your password? This will log you out and send a verification link to your email.',
      okText: 'Reset Password',
      onOk: async () => {
        try {
          await forgotPassword(email)
          addToast('Password reset email sent', 'success')
          navigate('/logout')
        } catch (e) {
          addToast(e instanceof Error ? e.message : 'Unable to reset password', 'error')
          throw e
        }
      },
    })
  }

  return (
    <div className="relative overflow-hidden rounded-2xl border border-brand-100 shadow-card bg-gradient-to-br from-white to-sky-50/35 p-6">
      <div className="pointer-events-none absolute -top-20 -right-20 h-56 w-56 rounded-full bg-sky-100/60 blur-2xl" aria-hidden />
      <div className="relative z-10">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div>
            <h1 className="font-display text-2xl font-bold text-gray-900">My Profile</h1>
            <p className="text-sm text-gray-500 mt-1">Please ensure your details are correct and up to date.</p>
          </div>
          <Space>
            <Tooltip title="Refresh profile">
              <Tag
                color="cyan"
                className="cursor-pointer"
                onClick={() => void handleRefresh()}
              >
                <SyncOutlined className="mr-1" />
                Refresh
              </Tag>
            </Tooltip>
          </Space>
        </div>

        <Card loading={isLoading || isFetching} className="rounded-xl border border-brand-100">
          {!data ? (
            <Alert type="warning" showIcon message="Profile details are not available." />
          ) : (
            <Form form={form} layout="vertical">
              <div className="grid gap-4 md:grid-cols-2">
                <Form.Item
                  label="First Name"
                  name="firstName"
                  rules={[
                    { required: true, message: 'First name is required' },
                    { min: 2, message: 'First name must be at least 2 characters' },
                    { max: 50, message: 'First name must be 50 characters or fewer' },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item
                  label="Last Name"
                  name="lastName"
                  rules={[
                    { required: true, message: 'Last name is required' },
                    { min: 2, message: 'Last name must be at least 2 characters' },
                    { max: 50, message: 'Last name must be 50 characters or fewer' },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item label="Email Address" name="email" rules={[{ required: true, message: 'Email is required' }]}>
                  <Input disabled />
                </Form.Item>
                <Form.Item
                  label="Contact Number"
                  name="phone"
                  rules={[
                    { required: true, message: 'Contact number is required' },
                    {
                      validator: async (_, value: string) => {
                        if (normalizePhone(value).length === 10) return
                        throw new Error('Contact number must contain 10 digits')
                      },
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
                <Form.Item label="Default Page Size" name="defaultPageSize">
                  <Select options={pageSizeOptions} />
                </Form.Item>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <Button
                  icon={<ReloadOutlined />}
                  onClick={handleResetPassword}
                  disabled={updateProfileMutation.isPending}
                >
                  Reset Password
                </Button>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  loading={updateProfileMutation.isPending}
                  onClick={() => void handleSave()}
                >
                  Update
                </Button>
              </div>
            </Form>
          )}
        </Card>

        <AuthenticatorSettings
          autoStartSetup={autoStartAuthenticatorSetup}
          onAutoStartHandled={clearAuthenticatorSetupParam}
        />

        <McpProfileCard />

        {canUseAdvisory ? (
          <Card className="mt-6 rounded-xl border border-brand-100">
            <div className="mb-4">
              <h2 className="font-display text-lg font-bold text-gray-900">Avatar &amp; Voice</h2>
              <p className="text-sm text-gray-500">
                Used to generate Video Avatar Summaries in AdvicePrep. Shared with your AdvicePrep settings.
              </p>
            </div>
            <FeatureThemeProvider featureKey="advisoryIntel" withAntd>
              <AvatarVoicePanel />
            </FeatureThemeProvider>
          </Card>
        ) : null}
      </div>
    </div>
  )
}
